import { useEffect, useState } from "react";
import { X, Download, ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatApiError } from "@/lib/apiError";

/**
 * CatalogLightbox — fullscreen viewer for a single composed catalog item.
 *
 * Triggered by double-clicking any catalog grid tile. Lets the user
 * inspect the result at native resolution before deciding to download.
 *
 * Navigation:
 *   • ← / →  Arrow keys flip to the previous/next item in the same grid
 *   • Esc    Close
 *   • Click outside (overlay backdrop) → Close
 *   • Click the X button → Close
 *
 * Retry:
 *   • The "Retry" button re-runs the Gemini compose with the same
 *     scene + jewellery + prompt + format + quality + watermark
 *     settings. Cost = same as a fresh compose. The new item is a
 *     sibling tagged `retry_of: <original_id>` so the user can compare
 *     the two.
 */
export default function CatalogLightbox({ items, index, backendUrl, onClose, onNavigate, onRetried }) {
  const item = items?.[index];
  const total = items?.length || 0;
  const hasPrev = index > 0;
  const hasNext = index < total - 1;
  const [retrying, setRetrying] = useState(false);
  // Free-text refinement appended to the retry prompt. Persists per
  // lightbox-open session (cleared when the user navigates to another
  // item) so they can iterate without losing typed direction.
  const [retryExtra, setRetryExtra] = useState("");
  const [retryOpen, setRetryOpen] = useState(false);

  // Reset the extra-prompt and the popover state every time the
  // viewer flips to a different item — otherwise typed refinement
  // bleeds across siblings.
  useEffect(() => { setRetryExtra(""); setRetryOpen(false); }, [item?.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowLeft" && hasPrev) { onNavigate(index - 1); }
      else if (e.key === "ArrowRight" && hasNext) { onNavigate(index + 1); }
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll so the page underneath doesn't drift when scrolling
    // tall images inside the viewer.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, onNavigate, index, hasPrev, hasNext]);

  const canRetry = !!(item?.scene_gen_id && item?.jewellery_gen_id);

  const onRetry = async () => {
    if (!item || retrying) return;
    if (!canRetry) {
      toast.error("This item was composed before retry was supported. Re-upload from the catalog page.");
      return;
    }
    if (!window.confirm("Retry will spend 2 credits and create a new sibling image. Continue?")) return;
    setRetrying(true);
    try {
      const r = await axios.post(`${backendUrl}/api/catalog/items/${item.id}/retry`, {
        extra_prompt: (retryExtra || "").trim(),
      });
      toast.success("Retry complete. New version added next to the original.");
      setRetryExtra(""); setRetryOpen(false);
      onRetried?.(r.data?.item, item.id);
    } catch (err) {
      toast.error(formatApiError(err, "Retry failed."));
    } finally {
      setRetrying(false);
    }
  };

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-md flex items-center justify-center p-6 animate-in fade-in duration-200"
      onClick={onClose}
      data-testid="catalog-lightbox"
    >
      {/* Prev arrow */}
      {hasPrev && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNavigate(index - 1); }}
          aria-label="Previous"
          data-testid="catalog-lightbox-prev"
          className="absolute z-10 left-4 sm:left-8 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/15 bg-black/60 text-white/75 hover:text-white hover:border-white/40 backdrop-blur-sm transition"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {/* Next arrow */}
      {hasNext && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNavigate(index + 1); }}
          aria-label="Next"
          data-testid="catalog-lightbox-next"
          className="absolute z-10 right-4 sm:right-8 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/15 bg-black/60 text-white/75 hover:text-white hover:border-white/40 backdrop-blur-sm transition"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      <div onClick={(e) => e.stopPropagation()} className="relative max-w-6xl max-h-[92vh] w-full flex flex-col items-center">
        <button
          onClick={onClose}
          aria-label="Close preview"
          data-testid="catalog-lightbox-close"
          className="absolute -top-10 right-0 inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/15 text-white/75 hover:text-white hover:border-white/40 transition"
        >
          <X className="w-4 h-4" />
        </button>
        {total > 1 && (
          <span
            className="absolute -top-10 left-0 text-[11px] uppercase tracking-[0.22em] text-white/55 tabular-nums"
            data-testid="catalog-lightbox-counter"
          >
            {index + 1} / {total}
          </span>
        )}
        <img
          src={`${backendUrl}${item.image_url}`}
          alt={item.format_label}
          className="max-w-full max-h-[80vh] rounded-xl border border-white/10 object-contain bg-[#0A0A0A]"
          data-testid="catalog-lightbox-image"
        />
        <div className="mt-4 w-full flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xs text-white/65 leading-relaxed">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mr-2">
              {item.format_label}
            </span>
            <span className="tabular-nums">{item.width}×{item.height}</span>
            {item.quality && <span className="text-white/40"> · {item.quality}</span>}
            {item.watermarked && <span className="text-white/40"> · branded</span>}
            {item.prompt && (
              <span className="block text-white/45 mt-1 max-w-2xl">{item.prompt}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                type="button"
                onClick={() => {
                  if (!canRetry) {
                    toast.error("This item was composed before retry was supported.");
                    return;
                  }
                  setRetryOpen((v) => !v);
                }}
                disabled={retrying || !canRetry}
                data-testid="catalog-lightbox-retry"
                title={canRetry ? "Regenerate this image (optionally add extra direction)" : "Retry not available — item composed before retry was supported"}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 text-white/85 text-sm font-medium px-4 py-2.5 hover:bg-white/10 hover:text-white hover:border-white/30 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw className={`w-3.5 h-3.5 ${retrying ? "animate-spin" : ""}`} />
                {retrying ? "Retrying…" : "Retry"}
              </button>
              {retryOpen && canRetry && !retrying && (
                <div
                  data-testid="catalog-lightbox-retry-panel"
                  onClick={(e) => e.stopPropagation()}
                  // Open upward — the Retry button sits in the bottom
                  // toolbar of the lightbox, so a downward popover gets
                  // clipped off-screen unless the user zooms out.
                  className="absolute right-0 bottom-full mb-2 w-[320px] sm:w-[380px] rounded-xl border border-white/12 bg-[#141414] shadow-2xl shadow-black/60 backdrop-blur-md p-4 z-20 animate-in fade-in slide-in-from-bottom-2 duration-150"
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2">Refine this retry</div>
                  <textarea
                    value={retryExtra}
                    onChange={(e) => setRetryExtra(e.target.value)}
                    placeholder="Optional — add corrective direction. e.g. “make the chain thinner”, “tilt the ring slightly”, “warmer skin tone”…"
                    rows={3}
                    maxLength={400}
                    data-testid="catalog-lightbox-retry-extra"
                    className="w-full bg-[#0F0F0F] border border-white/10 rounded-md px-3 py-2 text-[13px] text-white placeholder-white/30 leading-snug focus:outline-none focus:border-[#D4AF37]/60 resize-none"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-white/35">
                      Added on top of the original prompt — original intent is kept.
                    </span>
                    <span className="text-[10px] text-white/45 tabular-nums">{retryExtra.length}/400</span>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => { setRetryExtra(""); setRetryOpen(false); }}
                      className="flex-1 text-[11.5px] px-3 py-1.5 rounded-md border border-white/10 text-white/70 hover:bg-white/5 hover:text-white transition"
                      data-testid="catalog-lightbox-retry-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={onRetry}
                      data-testid="catalog-lightbox-retry-go"
                      className="flex-1 text-[11.5px] px-3 py-1.5 rounded-md bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition"
                    >
                      Retry · 2 credits
                    </button>
                  </div>
                </div>
              )}
            </div>
            <a
              href={`${backendUrl}${item.image_url}?download=1`}
              download={`${item.id}.${item.file_format || "png"}`}
              data-testid="catalog-lightbox-download"
              className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium px-5 py-2.5 hover:bg-[#F3CE5A] transition"
            >
              <Download className="w-3.5 h-3.5" /> Download
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
